/**
 * Footer V6 — mobile disclosure for the four link groups.
 *
 * Below the `lg` breakpoint (992px) each group collapses to a full-width heading row that
 * toggles its list. At `lg` and above the groups are permanently expanded and carry NO
 * aria-expanded at all (acceptance criterion 5), so the disclosure is *built* below lg and
 * *torn down* above it rather than merely being restyled.
 *
 * The markup ships expanded and buttonless. Without JavaScript every link stays readable —
 * which matters because V6 deliberately stopped hiding links at mobile (design.md D4).
 *
 * The affordance glyph is an 18x10 down-pointing chevron, drawn in the design inside every
 * collapsed row and reproduced in CSS (_Footer.scss). OQ11 is closed. The SEMANTICS below
 * never depended on it.
 */

const BREAKPOINT_LG = 992;

/**
 * THE ONE SOURCE OF TRUTH FOR "ARE WE BELOW lg?". Never read window.innerWidth for a
 * layout decision in this file.
 *
 * innerWidth INCLUDES the classic vertical scrollbar; a CSS `min-width` media query is
 * evaluated against the initial containing block, which EXCLUDES it — about 15px apart on
 * Windows and Linux. So `window.innerWidth < BREAKPOINT_LG` and the stylesheet disagreed
 * across a ~15px band: at innerWidth 992-1006 the CSS still applied the single-column
 * stack while this file ran expand() and tore the toggles down, leaving all four groups
 * fully expanded in one column with no disclosure controls at all. macOS has overlay
 * scrollbars and never reproduces it, which is why local testing missed it.
 *
 * matchMedia evaluates the same query the CSS does, so the two cannot drift.
 */
const mq = window.matchMedia(`(min-width: ${BREAKPOINT_LG}px)`);

const SELECTORS = {
  group: '.FooterLinkGroups__Group',
  heading: '.FooterLinkGroups__Heading',
  headingText: '.FooterLinkGroups__HeadingText',
  list: '.FooterLinkGroups__List',
};

/** Turn a group's heading into a disclosure button controlling its list. */
const collapse = function (group) {
  const heading = group.querySelector(SELECTORS.heading);
  const text = group.querySelector(SELECTORS.headingText);
  const list = group.querySelector(SELECTORS.list);
  if (!heading || !text || !list || heading.querySelector('button')) {
    return;
  }

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'FooterLinkGroups__Toggle';
  button.setAttribute('aria-expanded', 'false');
  button.setAttribute('aria-controls', list.id);
  button.appendChild(text);
  heading.appendChild(button);

  group.classList.add('FooterLinkGroups__Group--collapsible');
  list.hidden = true;

  button.addEventListener('click', function () {
    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    list.hidden = expanded;
  });
};

/** Restore the permanently-expanded desktop state, leaving no aria-* behind. */
const expand = function (group) {
  const heading = group.querySelector(SELECTORS.heading);
  const list = group.querySelector(SELECTORS.list);
  const button = heading && heading.querySelector('button');
  if (!heading || !list) {
    return;
  }

  if (button) {
    const text = button.querySelector(SELECTORS.headingText);
    if (text) {
      heading.appendChild(text);
    }
    button.remove();
  }

  group.classList.remove('FooterLinkGroups__Group--collapsible');
  list.hidden = false;
};

const apply = function () {
  const groups = document.querySelectorAll(SELECTORS.group);
  if (!groups.length) {
    return;
  }
  const collapsed = !mq.matches;
  groups.forEach(function (group) {
    if (collapsed) {
      collapse(group);
    } else {
      expand(group);
    }
  });
};

export const initialize = function () {
  if (!document.querySelector(SELECTORS.group)) {
    return;
  }
  apply();

  // matchMedia rather than a resize listener: this only has to fire when the breakpoint
  // is actually crossed, not on every pixel of a drag. The same object apply() reads, so
  // the event and the decision can never disagree.
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', apply);
  } else if (typeof mq.addListener === 'function') {
    mq.addListener(apply);
  }
};
